import { NavLink } from 'react-router-dom';
import { Presentation } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useClassroomStore } from '../../features/classroom/store';

/**
 * The instructor's "Teach" mode switch.
 *
 * Gold-treated so it reads as a MODE (design/Main.dc.html annotation), not as
 * another destination — which is why it does not live in the destination row
 * with Class, Learner plan and the rest. It sat at the right-hand end of that
 * row until the instructor asked for it "beside Theme"; row 1 is where the
 * other mode-ish controls (theme, focus, account) already are, and putting it
 * there also stops it competing for horizontal space with the destinations on
 * a narrow window.
 *
 * No `end`, deliberately — every /teach/* sub-page keeps Teach current, unlike
 * the plain destinations.
 *
 * The live dot is READ-ONLY: LiveClassBar (mounted alongside this in AppShell)
 * owns starting and stopping the shared 5s poll (features/classroom/store.ts).
 * This borrows the resulting state; it starts no second poll and duplicates no
 * action logic from RunSheetActionBar.
 */
export default function TeachButton() {
  const user = useAuthStore((s) => s.user);
  const isInstructor = !!(user?.is_instructor || user?.is_admin || user?.is_super_admin);
  const liveSession = useClassroomStore((s) => s.session);
  const isLive = isInstructor && liveSession?.status === 'live';

  if (!isInstructor) return null;

  return (
    <NavLink
      to="/teach"
      className={({ isActive }) =>
        `inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[13px] font-semibold transition-colors bg-[var(--ws-gold-quiet)] text-[var(--ws-gold-ink)] border-[color:var(--ws-gold)]/50 hover:border-[color:var(--ws-gold)] ${
          isActive ? 'ring-1 ring-[color:var(--ws-gold)] ring-offset-1 ring-offset-ws-nav' : ''
        }`
      }
    >
      <Presentation size={15} className="shrink-0" aria-hidden="true" />
      <span className="hidden sm:inline">Teach</span>
      {isLive && (
        <>
          <span
            className="size-1.5 shrink-0 animate-pulse rounded-full bg-ws-danger"
            aria-hidden="true"
          />
          <span className="sr-only"> — class is live</span>
        </>
      )}
    </NavLink>
  );
}
